import {Lang} from 'yougile-lang'
import {ObjUtil, Time} from 'yougile-util'

const scheduleArray = ['daily', 'weekly', 'monthly', 'yearly'] as const
export type ScheduleType = typeof scheduleArray[number]

export const selectedDayNumberArr = [1, 2, 3, 4, 5, 'last'] as const
export type SelectedDayNumber = typeof selectedDayNumberArr[number]

export const selectedDayTypeArr = [
  0, // Monday
  1,
  2,
  3,
  4,
  5,
  6, // Sunday
  'day',
  'weekday',
  'weekend'
] as const
export type SelectedDayType = typeof selectedDayTypeArr[number]

export type SelectedDayOfMonth = {
  dayNumber: SelectedDayNumber
  dayType: SelectedDayType
}

export interface Schedule {
  type: ScheduleType
  each?: number // 1 - 999
  since?: number
  minute: number // 0 - 59
  hour: number // 0 - 23
  daysOfMonth?: number[] // 1 - 31
  daysOfWeek?: number[] // Monday = 0, …, Sunday = 6
  monthsOfYear?: number[] // 1 - 12
  selectedDayOfMonth?: SelectedDayOfMonth
}

/**
 * TODO: implement support of timezones with half and quarter
 * of hour differences
 */
export const toServerSchedule = (s: Schedule, noTimeZone = false): Schedule => {
  if (!s) {
    return s
  }
  const diff = Time.getTimeDiff() + (noTimeZone ? 0 : Time.getTimeZoneDiff())
  const timezoneHourDiff = Math.round(diff / Time.hour)
  const hour = (s.hour + 24 - timezoneHourDiff) % 24
  return {...s, hour}
}

export const toLocalSchedule = (
  schedule: Schedule,
  noTimeZone = false
): Schedule => {
  if (!schedule) {
    return schedule
  }
  const diff = Time.getTimeDiff() + (noTimeZone ? 0 : Time.getTimeZoneDiff())
  const timezoneHourDiff = Math.round(diff / Time.hour)
  const hour = (schedule.hour + 24 + timezoneHourDiff) % 24
  return {...schedule, hour}
}

export const getGenderOfDayType = (dayType: SelectedDayType): 0 | 1 | 2 =>
  dayType === 6 ? 2 : [2, 4, 5].includes(dayType as number) ? 1 : 0

export const scheduleToReadable = (serverSch: Schedule | undefined): string => {
  if (!serverSch) {
    return ''
  }
  const sch = toLocalSchedule(serverSch)
  const zz = (n: number) => ('0' + n).slice(-2)
  const time = zz(sch.hour) + ':' + zz(sch.minute)
  const {each = 1} = serverSch
  switch (sch.type) {
    case 'daily':
      return Lang.msg.repeat.scheduleDaily(time, each)
    case 'weekly': {
      const weekDays = Lang.msg.weekNamesShort.filter((_v, ix) =>
        sch.daysOfWeek?.includes(ix)
      )
      return Lang.msg.repeat.scheduleWeekly(weekDays.join(', '), time, each)
    }
    case 'monthly':
      return scheduleToMonthlyReadable(sch, time)
    case 'yearly':
      return scheduleToYearlyReadable(sch, time)
    default:
      return '???'
  }
}

const scheduleToMonthlyReadable = (sch: Schedule, time: string) => {
  const {each = 1} = sch
  if (sch.selectedDayOfMonth) {
    const {dayType, dayNumber} = sch.selectedDayOfMonth
    const selectedDayNumber = Lang.msg.report.selectedDayNumber(
      String(dayNumber),
      getGenderOfDayType(dayType)
    )
    const selectedDayType = Lang.msg.report.selectedDayType(String(dayType))
    return Lang.msg.repeat.scheduleMonthlyWithSelectedDay(
      `${selectedDayNumber} ${selectedDayType}`,
      time,
      each
    )
  }
  if (sch.daysOfMonth) {
    return Lang.msg.repeat.scheduleMonthly(
      sch.daysOfMonth.sort((a, b) => a - b).join(', '),
      time,
      each
    )
  }
  return ''
}

const scheduleToYearlyReadable = (sch: Schedule, time: string) => {
  if (!sch.monthsOfYear || !(sch.selectedDayOfMonth || sch.daysOfMonth)) {
    return ''
  }

  const {each = 1} = sch
  const monthsOfYear = sch.monthsOfYear
    .sort((a, b) => a - b)
    .map(month => Lang.msg.months[month - 1])
    .join(', ')

  if (sch.selectedDayOfMonth) {
    const {dayType, dayNumber} = sch.selectedDayOfMonth
    const selectedDayNumber = Lang.msg.report.selectedDayNumber(
      String(dayNumber),
      getGenderOfDayType(dayType)
    )
    const selectedDayType = Lang.msg.report.selectedDayType(String(dayType))
    return Lang.msg.repeat.scheduleYearlyWithSelectedDay(
      monthsOfYear,
      `${selectedDayNumber} ${selectedDayType}`,
      time,
      each
    )
  }

  if (sch.daysOfMonth) {
    return Lang.msg.repeat.scheduleYearly(
      monthsOfYear,
      sch.daysOfMonth.sort((a, b) => a - b).join(', '),
      time,
      each
    )
  }
  return ''
}

export const shiftScheduleTime = <T extends {hour: number; minute: number}>(
  sc: T,
  shiftMillis: number
): T => {
  if (!sc) {
    return sc
  }
  const t1 = new Date()
  t1.setHours(sc.hour || 0)
  t1.setMinutes(sc.minute || 0)
  const t2 = new Date(t1.getTime() + shiftMillis)
  return {...sc, hour: t2.getHours(), minute: t2.getMinutes()}
}

export const isSchedule = (schedule: any): schedule is Schedule => {
  const s = schedule as Schedule
  return (
    s?.type &&
    scheduleArray.includes(s.type) &&
    ObjUtil.isDefined(s.hour) &&
    ObjUtil.isDefined(s.minute)
  )
}

export const getTimeOfDayWithScheduleTime = (
  schedule: Schedule,
  time = Time.now()
): number => {
  return (
    Time.startOfDayOnServer(time) +
    (schedule.hour * Time.hour + schedule.minute * Time.minute)
  )
}

export const updateDefaultScheduleProps = (schedule: Schedule): void => {
  schedule.each ||= 1
  schedule.since = getTimeOfDayWithScheduleTime(
    toLocalSchedule(schedule),
    schedule.since ?? Time.now()
  )
}
